import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { ShopifyProduct } from "@/lib/shopify";

export interface WishlistItem {
  id: string; // Shopify product GID
  handle: string;
  title: string;
  productType: string;
  price: number;
  image: string;
}

interface WishlistContextType {
  items: WishlistItem[];
  addToWishlist: (product: ShopifyProduct) => void;
  removeFromWishlist: (id: string) => void;
  isInWishlist: (id: string) => boolean;
  toggleWishlist: (product: ShopifyProduct) => void;
  clearWishlist: () => void;
  totalItems: number;
}

const STORAGE_KEY = "alluring-wishlist";

const WishlistContext = createContext<WishlistContextType | undefined>(undefined);

export const WishlistProvider = ({ children }: { children: ReactNode }) => {
  const [items, setItems] = useState<WishlistItem[]>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addToWishlist = (product: ShopifyProduct) => {
    const { node } = product;
    setItems((prev) => {
      if (prev.find((i) => i.id === node.id)) return prev;
      return [...prev, {
        id: node.id,
        handle: node.handle,
        title: node.title,
        productType: node.productType || "",
        price: parseFloat(node.priceRange.minVariantPrice.amount),
        image: node.images.edges[0]?.node.url || "",
      }];
    });
  };

  const removeFromWishlist = (id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  };

  const isInWishlist = (id: string) => items.some((i) => i.id === id);

  const toggleWishlist = (product: ShopifyProduct) => {
    if (isInWishlist(product.node.id)) {
      removeFromWishlist(product.node.id);
    } else {
      addToWishlist(product);
    }
  };

  const clearWishlist = () => setItems([]);

  return (
    <WishlistContext.Provider
      value={{
        items,
        addToWishlist,
        removeFromWishlist,
        isInWishlist,
        toggleWishlist,
        clearWishlist,
        totalItems: items.length,
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error("useWishlist must be used within a WishlistProvider");
  }
  return context;
};
